/**
 * JS Class implementation of handful helper methods
 * Section Class is exported to window.bluedgeusa.core namespace
 *
 * This class use STATIC Methods.
 *
 * NOTE: Need Polyfill for older browsers
 */
'use strict';

class Utils {
	/**
	 * Abstract JS native fetch method
	 * Use Async / await
	 * Use: `options.noOption: true` to use default fetch options
	 * @param {Object} options - Fetch call options
	 * @param {Object} data - required - pass null if no data
	 * @param {Function} onSuccess
	 * @param {Function} onFail
	 * @param {Function} onCompleted
	 */
	static fetch(options, data = null, onSuccess, onFail, onCompleted) {
		// Define Async call
		function HTTPcall(url, options, data) {
			const settings = (options.noOption === true) ? null : {
				method: options.method || 'GET',
				credentials: options.credentials || 'same-origin',
				mode: options.mode || 'cors',
				headers: {
					'Content-Type': options.contentType || 'application/json',
					'Cache-Control': options.cache || 'max-age=0',
					'X-Requested-With': 'XMLHttpRequest' // This is needed as currently there is a bug in Shopify that assumes this header
				}
			};

			// ADD body only if data
			if (data)
				settings.body = JSON.stringify(data);

			fetch(options.url, settings).then(response => {
				// Get content from headers
				let _type = 'application/json'; // Default

				if (settings && typeof settings.headers['Content-Type'] !== 'undefined')
					_type = settings.headers['Content-Type'];
				else if (typeof response.headers.get('Content-Type') !== 'undefined')
					_type = response.headers.get('Content-Type');

				// Normal request type 
				if (!response.ok) {
					// JSON get details
					if (_type === 'application/json' && response.body)
						return response.json();
					else
						return Promise.reject(response);
				} else {
					if (_type === 'application/json')
						return response.json();
					else
						return response.text();
				}
			})
				.then((r) => {
					// Fail				
					if ((r.ok !== true && r.status > 299) || r.status === 422) {
						onFail && onFail(r);
					} else { // Success
						onSuccess && onSuccess(r);
					}
				})
				.catch((error) => {
					onFail && onFail(error);
				})
				.finally(() => {
					onCompleted && onCompleted();
				})
		}

		// Call the function
		HTTPcall(options.url, options, data, onSuccess, onFail);
	}

	/**
	 * Call to JS API Add to Cart
	 * 
	 * Receive an array of items
	 * [{ 
	 *   quantity: qty,
	 *   id: variantId,
	 *   properties: properties (optional)
	 *   selling_plan: sellingPlan (optional)
	 * }]
	 * 
	 * EVENT: Dispatch events
	 * 		- Start: core:addToCart:start
	 * 		- Success: core:addToCart:success
	 * 		- Fail: core:addToCart:fail
	 * 		- Completed: core:addToCart:completed
	 * @param {Array} items
	 * @returns {Boolean}
	 */
	static addToCart(items) {
		try {
			if (!items || !Array.isArray(items))
				throw Error('Utils::addToCart:invalid items input');

			// Convert to data array to object
			const data = { items: items };

			// Custom Event
			// Start: core:addToCart:start
			document.dispatchEvent(new CustomEvent('core:addToCart:start'));

			// Fetch
			// Options, data, onSuccess, onFail
			Utils.fetch(
				{
					method: 'POST',
					url: KROWN.settings.routes.cart_add_url + '.js'
				},
				data,
				// onSuccess
				(response) => {
					document.dispatchEvent(
						new CustomEvent('core:addToCart:success', { detail: response })
					);
				},
				// onFail
				(response) => {
					document.dispatchEvent(
						new CustomEvent('core:addToCart:fail', { detail: { err: response } })
					);
				},
				// onCompleted
				() => {
					document.dispatchEvent(
						new CustomEvent('core:addToCart:completed')
					);
				}
			);
			return true;

		} catch (err) {
			console.log(err);
		}
	}

	/**
	 * Call to JS API change Cart
	 * qty: 0: to delete a variant
	 *
	 * Note: We rely on Key not variant ID to avoid possible confusion and later reference
	 *
	 * EVENT: Dispatch events
	 * 		- Start: core:changeToCart:start
	 * 		- Success: core:changeToCart:success
	 * 		- Fail: core:changeToCart:fail
	 *
	 * @param {Integer} qty
	 * @param {Integer} key - variant Key 
	 * @param {Object} properties - optional
	 * @param {Integer} sellingPlan - optional
	 * @param {String} action - (plus | minus | remove)
	 * @returns {Boolean}
	 */
	static changeToCart(qty, key = null, properties = null, sellingPlan = null, action) {
		if (typeof qty === 'undefined' || !key) return false;
		let data = {}, deleteAction = false;

		// Custom Event
		// Start: core:addToCart:start
		document.dispatchEvent(new CustomEvent('core:changeToCart:start'));

		// Define variant to update
		if (key)
			data = { id: key, quantity: ~~qty, action: action };
		else
			data = { id: this._currentVariant.key, quantity: ~~qty, action: action };

		// Add Properties to data
		if (properties)
			data = Object.assign(data, properties);

		// Add Selling Plan
		if (sellingPlan)
			data.selling_plan = sellingPlan;

		// Set Delete action if qty === 0
		if (~~qty === 0)
			deleteAction = true;

		// Fetch
		// Options, data, onSuccess, onFail
		Utils.fetch(
			{
				method: 'POST',
				url: KROWN.settings.routes.cart_change_url + '.js'
			},
			data,
			function onSuccess(response) {
				// Add data to response - for later use
				response.data = data;

				// Event for delete
				if (deleteAction) {
					document.dispatchEvent(
						new CustomEvent('core:changeToCart:delete:success', { detail: response })
					);
				} else {
					document.dispatchEvent(
						new CustomEvent('core:changeToCart:success', { detail: response })
					);
				}
			},
			function onFail(err) {
				// Add data to response - for later use
				err.data = data;

				document.dispatchEvent(
					new CustomEvent('core:changeToCart:fail', { detail: { err: err, key: key } })
				);
			}
		);
		return true;
	}

	/**
	 * Call to JS API Clear Cart
	 * EVENT: Dispatch events
	 * 		- Start: core:clearCart:start
	 * 		- Success: core:clearCart:success
	 * 		- Fail: core:clearCart:fail
	 * @returns {Boolean}
	 */
	static clearCart() {
		document.dispatchEvent(new CustomEvent('core:clearCart:start'));
		Utils.fetch(
			{
				method: 'POST',
				url: KROWN.settings.routes.cart_clean + '.js'
			},
			null,
			function onSuccess(response) {
				document.dispatchEvent(
					new CustomEvent('core:clearCart:success', { detail: response })
				);
			},
			function onFail() {
				console.log("Utils::clearCart", "Sorry, an error occurred on cleaning cart.")
			}
		);
		return true;
	}
	
	/**
	 * Debounce method - static
	 * @param {Function} func
	 * @param {Integer} wait
	 * @param {Boolean} immediate
	 */
	static debounce(func, wait=400, immediate) {
		let timeout;
		return function() {
			const args = arguments;
			
			const later = () => {
				timeout = null;
				if (!immediate) 
					func.apply(this, args);
			};
		
			const callNow = immediate && !timeout;
			
			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
			if (callNow) 
				func.apply(this, args);
		}
	}

	/**
	 * Format money values based on your shop currency settings
	 * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents or 3.00 dollars
	 * @param  {String} format - Optional if different from the Store general settings
	 * @returns {String} value - formatted value
	 */
	static formatMoney(cents, format) {
		let storeMoneyFormat = KROWN.settings.shop_money_format || '${{amount}}';
		let value = '';
		const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

		// /if not price return empty string
		// Used for empty compared at price
		if (!cents && cents !== 0)
			return '';

		// Change default Money format if provided
		if (format)
			storeMoneyFormat = format;

		// Format string
		if (typeof cents === 'string')
			cents = cents.replace('.', '');

		// Return final formatting
		switch (storeMoneyFormat.match(placeholderRegex)[1]) {
			case 'amount':
				value = this._formatWithDelimiters(cents, 2);
				break;
			case 'amount_no_decimals':
				value = this._formatWithDelimiters(cents, 0);
				break;
			case 'amount_with_comma_separator':
				value = this._formatWithDelimiters(cents, 2, '.', ',');
				break;
			case 'amount_no_decimals_with_comma_separator':
				value = this._formatWithDelimiters(cents, 0, '.', ',');
				break;
		}

		return storeMoneyFormat.replace(placeholderRegex, value);
	}

	/**
	 * Format with delimiters
	 * @param {Number} number
	 * @param {Number} precision
	 * @param {String} thousands
	 * @param {String} decimal
	 * @returns {Float}
	 */
	static _formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
		if (isNaN(number) || number == null)
			return 0;

		number = (number / 100.0).toFixed(precision);
		const parts = number.split('.');
		const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
		const centsAmount = parts[1] ? decimal + parts[1] : '';

		return dollarsAmount + centsAmount;
	}

	/**
	 * Dynamically create a toast for displaying small messages across the page
	 * @param {String} message 
	 * @param {String} type 
	 * @param {Number} delay 
	 */
	static toast(message, type = null, delay = null) {
		const toasts = document.querySelector("[data-toasts]");

		// 1. Create Element
		const toastWrapper = document.createElement("div");

		// 2. Add Classes
		toastWrapper.classList.add("toast");

		// 2.2 Add type of class (info, warning, etc...)
		if (type === "info") 
			toastWrapper.classList.add("info");
		else if (type === "success")
			toastWrapper.classList.add("success");
		else if (type === "warn")
			toastWrapper.classList.add("warn");
		else if (type === "error") 
			toastWrapper.classList.add("error");

		// 3. Add test
		toastWrapper.innerText = message;

		// 4. Append toast msg
		toasts.append(toastWrapper);

		// 5. Remove toast after a while
		if (delay) {
			setTimeout(() => {
				toastWrapper.remove();
			}, delay);
		}
	}

	/**
	 * Build URL, using provided Section ID and params if given
	 * @param {String} searchParams (optional)
	 * @param {String} optionalSectionId (optional)
	 * @returns {String}
	 */
	static buildURL(searchParams = null, optionalSectionId = null) {
		// Return URL
		return `${window.location.pathname}?section_id=${optionalSectionId}${searchParams ? '&' + searchParams : ''}`;
	}
}


window.bluedgeusa.theme.Utils = Utils;